import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { AlertTriangle, Send, Clock, Link as LinkIcon, ChevronRight, Wand2, Copy } from "lucide-react";

const clientTz = Intl.DateTimeFormat().resolvedOptions().timeZone;

export default function DiscoveryCallsQueue() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [defaultTemplate, setDefaultTemplate] = useState("");
  const [editingLink, setEditingLink] = useState("");
  const [editingInstr, setEditingInstr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [{ data }, { data: tpl }] = await Promise.all([
        api.get("/appointments/upcoming-queue"),
        api.get("/notification-templates/call-instructions"),
      ]);
      setRows(data);
      setDefaultTemplate(tpl.content || "");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, []);

  const openDialog = (r) => {
    setOpenId(r.id);
    setEditingLink(r.call_link || "");
    setEditingInstr(r.call_instructions || defaultTemplate);
  };

  const saveDraft = async () => {
    setBusy(true);
    try {
      await api.patch(`/appointments/${openId}/link-instructions`, {
        call_link: editingLink, call_instructions: editingInstr,
      });
      toast.success("Saved");
      load();
    } catch (err) { toast.error("Failed"); } finally { setBusy(false); }
  };

  const sendNow = async () => {
    if (!editingLink) { toast.error("Paste the call link first"); return; }
    setBusy(true);
    try {
      // save then send
      await api.patch(`/appointments/${openId}/link-instructions`, {
        call_link: editingLink, call_instructions: editingInstr,
      });
      const { data } = await api.post(`/appointments/${openId}/send-link`);
      const channels = (data.channels || []).join(" + ") || "(none)";
      toast.success(`Sent via ${channels}`);
      setOpenId(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="overline mb-1 text-muted-foreground">Stage 1 · Discovery Calls</p>
        <h1 className="font-dashboard text-3xl font-bold tracking-tight">Upcoming Discovery Calls</h1>
        <p className="mt-1 text-sm text-muted-foreground">Paste the meeting link and send it to the client. Flags escalate as the call time approaches.</p>
      </div>

      <Card className="border-border bg-white">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-sm">
            <thead>
              <tr className="border-b border-border bg-secondary/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3">When (client TZ)</th>
                <th className="px-4 py-3">Client</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Link</th>
                <th className="px-4 py-3">Reminder</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody data-testid="discovery-queue-body">
              {loading ? <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">Loading…</td></tr> :
                rows.length === 0 ? <tr><td colSpan={6} className="px-4 py-16 text-center text-muted-foreground">No Discovery Calls in the queue.</td></tr> :
                rows.map((r) => {
                  const clientTzR = r.client_timezone || clientTz;
                  const when = new Date(r.slot_datetime_utc).toLocaleString("en-IN", {
                    weekday: "short", day: "2-digit", month: "short",
                    hour: "2-digit", minute: "2-digit", timeZone: clientTzR,
                  });
                  const urgencyColor = r.urgency === "red" ? "text-red-600 bg-red-50 border-red-200" :
                                       r.urgency === "amber" ? "text-amber-700 bg-amber-50 border-amber-200" : "";
                  return (
                    <tr key={r.id} className="border-b border-border last:border-0 hover:bg-secondary/20" data-testid={`queue-row-${r.id}`}>
                      <td className="px-4 py-4">
                        <div className="font-medium">{when}</div>
                        <div className="text-xs text-muted-foreground">{clientTzR} · in {r.hours_to_call}h</div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="font-medium">{r.prospect?.full_name || "—"}</div>
                        <div className="text-xs text-muted-foreground">{r.prospect?.mobile} · {r.prospect?.email}</div>
                      </td>
                      <td className="px-4 py-4">
                        <span className={
                          "rounded-full border px-2 py-0.5 text-xs " +
                          (r.status === "Link Sent" ? "border-primary/30 bg-primary/10 text-primary" :
                           r.status === "Rescheduled" ? "border-accent/40 bg-accent/10 text-accent" :
                           "border-amber-200 bg-amber-50 text-amber-700")
                        }>{r.status}</span>
                      </td>
                      <td className="px-4 py-4 text-xs">
                        {r.call_link ? (
                          <a href={r.call_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline">
                            <LinkIcon className="h-3 w-3" /> open
                          </a>
                        ) : <span className="text-muted-foreground">— not set</span>}
                      </td>
                      <td className="px-4 py-4">
                        {r.urgency && (
                          <span className={"inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs " + urgencyColor} data-testid={`urgency-${r.urgency}-${r.id}`}>
                            <AlertTriangle className="h-3 w-3" /> {r.urgency === "red" ? "4h — send now" : "24h — link pending"}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-right">
                        <Button size="sm" onClick={() => openDialog(r)} className="gap-1" data-testid={`open-appt-${r.id}`}>
                          {r.status === "Link Sent" ? "Resend" : "Paste link"} <ChevronRight className="h-3 w-3" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={!!openId} onOpenChange={(o) => { if (!o) setOpenId(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Send call link & instructions</DialogTitle></DialogHeader>
          <div className="space-y-4 py-3">
            <div>
              <Label>Call link (paste Proton Meet / other)</Label>
              <Input value={editingLink} onChange={(e) => setEditingLink(e.target.value)} placeholder="https://meet.example.com/xyz" data-testid="paste-link-input" />
            </div>
            <div>
              <div className="mb-2 flex items-center justify-between">
                <Label>Instructions to client</Label>
                <button
                  type="button"
                  onClick={() => setEditingInstr(defaultTemplate)}
                  className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                  data-testid="reset-template-btn"
                >
                  <Wand2 className="h-3 w-3" /> reset to default template
                </button>
              </div>
              <Textarea rows={11} value={editingInstr} onChange={(e) => setEditingInstr(e.target.value)} data-testid="paste-instructions-input" className="font-mono text-xs" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={saveDraft} disabled={busy} data-testid="save-draft-btn"><Copy className="mr-1 h-3 w-3" /> Save draft</Button>
            <Button onClick={sendNow} disabled={busy || !editingLink} className="gap-1" data-testid="send-now-btn">
              <Send className="h-3 w-3" /> {busy ? "Sending…" : "Send link & instructions"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
